import {
    AsymmetricDataEncryptor,
    EncryptedData
} from "./asymmetric-data-encryptor";
import { AsymmetricDataSigner, SignedData } from "./asymmetric-data-signer";
import {
    ApplicationDataEncryptor,
    ApplicationDataEncryptorI
} from "./application-data-encryptor";
import { JSONValue } from "encoding/json/types";
import { ExportedKey } from "encoding/key";

export interface DataEncryptorI {
    encrypt(jsonableObj: JSONValue): Promise<EncryptedData>;
    decrypt(encryptedData: EncryptedData): Promise<JSONValue>;
    update(
        newData: JSONValue,
        existingEncryptedObject: EncryptedData,
        encryptedKeyOverride?: string
    ): Promise<EncryptedData>;
    share(
        encryptedObject: EncryptedData,
        publicKeyObj: JsonWebKey
    ): Promise<string>;
    exportPublicKey(): Promise<JsonWebKey>;

    sign(jsonableInput: JSONValue): Promise<SignedData>;
    verify(signedObject: SignedData): Promise<JSONValue>;
    verifyAgainst(
        publicKeyObj: ExportedKey,
        signedObject: SignedData
    ): Promise<JSONValue>;

    createApplicationEncryptor(): Promise<ApplicationDataEncryptorI>;
    loadApplicationEncryptorForEncryption(
        publicKeyObj: JsonWebKey
    ): Promise<ApplicationDataEncryptorI>;
    loadApplicationEncryptorForDecryption(
        publicKeyObj: JsonWebKey,
        encryptedPrivateKey: EncryptedData
    ): Promise<ApplicationDataEncryptorI>;
}

export function DataEncryptor(
    encryptionKeyPair: CryptoKeyPair,
    signingKeyPair: CryptoKeyPair
): DataEncryptorI {
    const asymmetricDataEncryptor = AsymmetricDataEncryptor(encryptionKeyPair);
    const asymmetricDataSigner = AsymmetricDataSigner(signingKeyPair);

    return {
        // Symmetric key encryption
        encrypt: asymmetricDataEncryptor.encrypt,
        decrypt: asymmetricDataEncryptor.decrypt,
        update: asymmetricDataEncryptor.update,
        share: asymmetricDataEncryptor.share,
        exportPublicKey: asymmetricDataEncryptor.exportPublicKey,

        // Asymmetric key signing
        sign: asymmetricDataSigner.sign,
        verify: asymmetricDataSigner.verify,
        verifyAgainst: AsymmetricDataSigner.verifyAgainst,

        // Application related stuff
        createApplicationEncryptor: ApplicationDataEncryptor.create,
        loadApplicationEncryptorForEncryption:
            ApplicationDataEncryptor.loadForEncryption,
        loadApplicationEncryptorForDecryption: function(
            publicKey: JsonWebKey,
            encryptedPrivateKey: EncryptedData
        ): Promise<ApplicationDataEncryptorI> {
            return ApplicationDataEncryptor.loadForDecryption(
                publicKey,
                encryptionKeyPair.privateKey,
                encryptedPrivateKey
            );
        }
    };
}
DataEncryptor.getDefaultAsymmetricEncryptionAlgorithm =
    AsymmetricDataEncryptor.getDefaultAlgorithm;
DataEncryptor.generateAsymmetricEncryptionKeyPair =
    AsymmetricDataEncryptor.generateKeyPair;
DataEncryptor.getDefaultSigningAlgorithm =
    AsymmetricDataSigner.getDefaultAlgorithm;
DataEncryptor.generateSigningKeyPair = AsymmetricDataSigner.generateKeyPair;
